import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from 'material-ui/styles';
import Input from 'material-ui/Input';
import Chip from 'material-ui/Chip';

export const styles = theme => ({
  root: {
    position: 'relative',
    height: '38px'
  },

  container: {
    borderRadius: 2,
    backgroundColor: theme.palette.common.white,
    border: '1px solid #ced4da',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    '&:focus': {
      borderColor: '#1A65FF',
    },
    width: '100%',
    height: '38px'
  },

  chipContainer: {
    display: 'flex',
    paddingLeft: '12px',
  },

  textRoot: {
    display: 'inline-flex',
    marginTop: 0
  },

  textInput: {
    width: '100%',
    minWidth: '50px',
    padding: '10px 12px 10px 0px',
    border: 'none',
    appearance: 'none', // Remove border in Safari, doesn't seem to break anything in other browsers
  },

  chipRoot: {
    marginRight: '5px',
    float: 'left',
    backgroundColor: 'rgba(26,101,255,.05)',
    border: '#1A65FF solid 1px',
    color: '#1A65FF',
    height: 'unset',
  },

  chipLabel: {
    padding: '5px 10px',
    fontSize: '11px',
  },

  dropdown: {
    backgroundColor: theme.palette.common.white,
    position: 'absolute',
    width: '100%',
    boxSizing: 'border-box',
    borderColor: '#ced4da',
    borderStyle: 'solid',
    borderWidth: '0px 1px 1px 1px',
    padding: '10px',
    fontSize: '11px',
    overflow: 'hidden',
  },

  input: {
    border: '0px',
  }
});

class ChipInput extends React.Component {
  state = {
    focusedChip: null,
    inputValue: '',
    isFocused: false,
    showDropdown: false,
  }

  handleAddChip = (chip) => {
    this.setState({ inputValue: '' })
    const chips = this.props.chips;

    if (chip === '') return;

    if (this.props.allowDuplicates || chips.indexOf(chip) === -1) {
      if (this.props.chips && this.props.onAdd) {
        this.props.onAdd(chip)
      } else {
        if (this.props.onChange) {
          this.props.onChange([...this.props.chips, chip])
        }
      }
    }
  }

  handleDeleteChip = (chip, i) => {
    const chips = [...this.props.chips];
    const changed = chips.splice(i, 1) // remove the chip at index i
    if (changed) {
      let focusedChip = this.state.focusedChip;

      if (this.state.focusedChip === i) {
        focusedChip = null
      } else if (this.state.focusedChip > i) {
        focusedChip = this.state.focusedChip - 1
      }

      this.setState({ focusedChip })

      if (this.props.onChange) {
        this.props.onChange(chips)
      }
    }
  }

  handleInputChange = (e) => {
    this.setState({
      inputValue: e.target.value,
      showDropdown: e.target.value && e.target.value !== '',
    })

    if (this.props.onUpdateInput) {
      this.props.onUpdateInput(e)
    }
  }

  handleKeyDown = (e) => {
    const { focusedChip } = this.state;
    const { chips } = this.props;

    const enterKeyCode = 13;
    const backspaceKeyCode = 8;
    const deleteKeyCode = 46;
    const leftArrowKeyCode = 37;
    const rightArrowKeyCode = 39;

    switch (e.keyCode) {
      case enterKeyCode: //Enter key
        this.handleAddChip(this.state.inputValue);
        this.clearInput();
        break;
      case leftArrowKeyCode: //back button
        if (focusedChip == null && e.target.value === '' && chips && chips.length) {
          return this.setState({ focusedChip: chips.length - 1 })
        }
        if (focusedChip != null && focusedChip > 0) {
          this.setState({ focusedChip: focusedChip - 1 })
        }
        break;
      case rightArrowKeyCode: //forward button
        if (focusedChip != null && focusedChip < chips.length - 1) {
          this.setState({ focusedChip: focusedChip + 1 })
        } else {
          this.setState({ focusedChip: null })
        }
        break;
      case backspaceKeyCode: //delete button
      case deleteKeyCode:
        if (e.target.value === '') { // when inputValue is empty
          this.clearInput();
          if (focusedChip == null) {
            this.setState({ focusedChip: chips.length - 1 })
          } else {
            const value = chips[focusedChip]
            this.handleDeleteChip(value, focusedChip)
          }
        }
        break;
      default:
        this.setState({ focusedChip: null });
        break;
    }
  }

  handleInputFocus = () => {
    this.setState({
      isFocused: true,
      focusedChip: null,
    });
  }

  handleInputBlur = () => {
    this.setState({
      isFocused: false,
      focusedChip: null,
    });

    if (this.state.inputValue !== '') {
      this.handleAddChip(this.state.inputValue);
    }
  }

  clearInput = () => {
    this.setState({
      inputValue: '',
      showDropdown: false,
    });
  }

  setInputRef = (node) => {
    this.input = node;
  }

  renderChip = ({ text, isFocused, isDisabled, handleClick }, key) => (
    <Chip
      key={key}
      color="primary"
      style={{
        pointerEvents: isDisabled ? 'none' : undefined,
        backgroundColor: isFocused ? 'rgba(26,101,255,.7)' : 'rgba(26,101,255,.05)',
        color: isFocused ? '#FFFFFF' : '#1A65FF',
      }}
      classes={{
        root: this.props.classes.chipRoot,
        label: this.props.classes.chipLabel,
      }}
      onClick={handleClick}
      label={text}
    />
  );

  render() {
    const {
      classes,
      disabled,
      chips,
      onChange: _onChange,
      id,
      placeholder,
      InputProps,
      ...rest
    } = this.props;

    const { showDropdown } = this.state;

    const hasInput = chips.length > 0 || this.state.inputValue.length > 0

    return (
      <div className={classes.root} {...rest}>
        <div
          className={classes.container}
          style={{
            borderColor: this.state.isFocused ? '#1A65FF' : '#ced4da'
          }}
        >
          <div className={classes.chipContainer}>
            {chips.map((tag, i) => {
              return this.renderChip({
                text: tag,
                isFocused: this.state.focusedChip === i,
                isDisabled: disabled,
                handleClick: () => {
                  if (this.input) this.input.focus();
                  this.setState({
                    focusedChip: this.state.focusedChip === i ? null : i,
                    isFocused: true,
                  });
                },
                handleDelete: () => this.handleDeleteChip(tag, i),
                className: classes.chip
              }, i)
            })}
          </div>
          <Input
            classes={{
              root: classes.textRoot,
              input: classes.textInput,
            }}
            id={id}
            value={this.state.inputValue}
            onChange={this.handleInputChange}
            onKeyDown={this.handleKeyDown}
            onFocus={this.handleInputFocus}
            onBlur={this.handleInputBlur}
            disabled={disabled}
            disableUnderline
            fullWidth={true}
            placeholder={!hasInput ? placeholder : null}
            inputRef={this.setInputRef}
            {...InputProps}
          />
        </div>
        <div className={classes.dropdown} style={{ display: showDropdown ? 'block' : 'none' }}>
          Enterを押して<b>{this.state.inputValue}</b>を追加
        </div>
      </div>
    );
  }
}

ChipInput.propTypes = {
  classes: PropTypes.object.isRequired,
  chips: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  allowDuplicates: PropTypes.bool,
  onUpdateInput: PropTypes.func,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onChange: PropTypes.func,
  InputProps: PropTypes.object,
  id: PropTypes.string,
}

ChipInput.defaultValue = {
  chips: [],
  disabled: false,
  placeholder: '',
  allowDuplicates: true,
}
export default withStyles(styles)(ChipInput);



// WEBPACK FOOTER //
// ./src/components/common/ChipInput.jsx